import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getUserList, getUser } from '../../redux/selector';
import apiServices from '../../services/service';
import { Ic } from '../../shared/component/GosIcon';
import AddBucketForm from './AddBucketForm';

/* GrowthOS — Bucket Management (design: growthos/BucketManagement.jsx).
   An overview (stat strip + search + card grid) and a per-bucket detail (hero +
   Leads / Object-Assignment tabs), wired to the REAL bucket APIs
   (getBucketsByUserId, getLeadsByBucket, getBucketObjects, addMappingToBucket).
   The design is a mock; fields with no backend (color, module, distribution
   rule, capacity/quota bar, active toggle, delete) are dropped rather than
   fabricated — the real bucket record only carries name / owner / access level /
   visibility / assigned users / expiry. Card accent color is derived
   deterministically. Reuses the shared .gos-tm CSS scope (same shell as Team
   Management) — no new CSS.
   Create + edit go through the shared AddBucketForm component (add/edit + Expires
   At + updateBucket/updateBucketMembers), NOT an inline drawer.
   Access-level rules (see CLAUDE.md "Bucket Management / Access Level model"):
   AddBucketForm only offers users whose access level is >= the bucket's, and the
   detail Leads tab only offers leads whose access level is <= the bucket's. */

const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#0e8c6f', '#2f7ea3', '#a35a2f'];
const hashIdx = (s, n) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % n;
};
const colorFor = (s) => AVA_COLORS[hashIdx(String(s || ''), AVA_COLORS.length)];
const initialsOf = (n) =>
  (n || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

const Avatar = ({ name, color, size = 30 }) => (
  <span
    className="gos-tm-ava"
    style={{ width: size, height: size, background: color || colorFor(name), fontSize: Math.round(size * 0.36) }}
    title={name}
  >
    {initialsOf(name)}
  </span>
);

/* skeleton atoms (shared .gos-skel-an shimmer) */
const Skel = ({ w, h = 10, r = 5, style }) => (
  <span className="gos-skel-an" style={{ display: 'block', width: w, height: h, borderRadius: r, ...style }} />
);

const BucketCardSkeleton = () => (
  <div className="gos-tm-card" style={{ cursor: 'default' }}>
    <div className="gos-tm-card-accent gos-skel-an" />
    <div className="gos-tm-card-head">
      <span className="gos-skel-an" style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <Skel w="60%" h={13} />
        <Skel w="40%" h={9} style={{ marginTop: 8 }} />
      </div>
    </div>
    <div className="gos-tm-leads">
      <div className="gos-tm-lead">
        <span className="gos-skel-an" style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <Skel w="55%" h={9} />
          <Skel w="35%" h={8} style={{ marginTop: 6 }} />
        </div>
      </div>
    </div>
    <div className="gos-tm-foot">
      <Skel w={40} h={20} r={999} />
      <Skel w={60} h={20} r={6} />
    </div>
  </div>
);

const TableSkeleton = ({ cols, rows = 6 }) => (
  <div className="gos-tm-tblscroll">
    <table className="gos-tm-tbl">
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            {Array.from({ length: cols }).map((_, c) => (
              <td key={c}>
                {c === 0 ? (
                  <div className="gos-tm-cellead">
                    <span className="gos-skel-an" style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0 }} />
                    <Skel w={140} h={10} />
                  </div>
                ) : (
                  <Skel w={`${70 - c * 6}%`} h={10} />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const nameOf = (u) =>
  `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.userName || u?.emailAddress || u?.email || '';

/* react-select style to match the design's inputs (Leads-tab assign picker) */
const gosSelect = {
  control: (base, s) => ({
    ...base, minHeight: 40, borderRadius: 10, fontSize: 13.5,
    borderColor: s.isFocused ? 'var(--gos-accent)' : 'var(--gos-border-strong)',
    boxShadow: s.isFocused ? '0 0 0 3px var(--gos-accent-soft)' : 'none',
    backgroundColor: 'var(--gos-surface)', '&:hover': { borderColor: 'var(--gos-accent)' },
  }),
  menu: (b) => ({ ...b, zIndex: 9999, backgroundColor: 'var(--gos-surface)', border: '1px solid var(--gos-border-strong)', borderRadius: 11, overflow: 'hidden' }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({ ...b, fontSize: 13.5, backgroundColor: s.isSelected ? 'var(--gos-accent-soft)' : s.isFocused ? 'var(--gos-surface-3)' : 'transparent', color: s.isSelected ? 'var(--gos-accent-ink)' : 'var(--gos-text)' }),
  placeholder: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
  singleValue: (b) => ({ ...b, color: 'var(--gos-text)' }),
  input: (b) => ({ ...b, color: 'var(--gos-text)' }),
  multiValue: (b) => ({ ...b, backgroundColor: 'var(--gos-accent-soft)', borderRadius: 6 }),
  multiValueLabel: (b) => ({ ...b, fontSize: 12, color: 'var(--gos-accent-ink)' }),
  indicatorSeparator: (b) => ({ ...b, backgroundColor: 'var(--gos-border)' }),
  dropdownIndicator: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
};

const ACCESS_RANK = { L1: 1, L2: 2, L3: 3 };
const isPrivate = (b) => String(b?.bucketVisibility || '').toLowerCase() === 'private';
const ownerIdOf = (b) => b?.bucketOwnerId ?? b?.ownerId ?? b?.bucketOwner ?? null;
const accessOf = (b) => b?.accessLevel || b?.access || '';

function BucketManagement({ embedded = false }) {
  const usersList = useSelector(getUserList);
  const userInfo = useSelector(getUser);
  const userId = userInfo?.user?.userId;
  const Wrap = ({ children }) => (embedded ? <>{children}</> : <div className="inner-page">{children}</div>);

  const userName = useMemo(() => {
    const m = {};
    (usersList || []).forEach((u) => { m[u.userId] = nameOf(u); });
    return m;
  }, [usersList]);

  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  // create/edit go through the shared AddBucketForm: formOpen + editData (null = add)
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const openCreate = () => { setEditData(null); setFormOpen(true); };
  const openEdit = (bucket) => { setEditData(bucket); setFormOpen(true); };

  const loadBuckets = () => {
    if (!userId) return;
    setLoading(true);
    apiServices
      .getBucketsByUserId(userId)
      .then((res) => {
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : raw?.bucketList || raw?.buckets || raw?.data || [];
        setBuckets(list);
      })
      .catch((e) => console.error('getBucketsByUserId', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadBuckets(); /* eslint-disable-next-line */ }, [userId]);

  const stats = useMemo(() => {
    const owners = new Set();
    let pub = 0;
    let priv = 0;
    buckets.forEach((b) => {
      if (ownerIdOf(b)) owners.add(ownerIdOf(b));
      if (isPrivate(b)) priv += 1; else pub += 1;
    });
    return { total: buckets.length, pub, priv, owners: owners.size };
  }, [buckets]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return buckets;
    return buckets.filter((b) =>
      `${b.name || ''} ${userName[ownerIdOf(b)] || ''} ${accessOf(b)} ${b.bucketVisibility || ''}`.toLowerCase().includes(s)
    );
  }, [buckets, q, userName]);

  const openBucket = buckets.find((b) => b.id === openId) || null;

  if (openBucket) {
    return (
      <Wrap>
        <div className="gos-tm">
          <BucketDetail
            bucket={openBucket}
            userName={userName}
            onBack={() => setOpenId(null)}
            onEdit={() => openEdit(openBucket)}
          />
        </div>
        <AddBucketForm
          open={formOpen}
          editData={editData}
          usersList={usersList}
          onClose={() => setFormOpen(false)}
          onSave={() => { setFormOpen(false); loadBuckets(); }}
        />
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="gos-tm">
        <div className="gos-tm-head">
          <div>
            <div className="gos-tm-title">Bucket Management</div>
            <div className="gos-tm-sub">Pool unassigned records and control how they&apos;re distributed to reps.</div>
          </div>
          <button type="button" className="gos-btn gos-btn-primary" onClick={openCreate}>
            <Ic name="plus" size={16} />New Bucket
          </button>
        </div>

        <div className="gos-tm-stats">
          {[
            ['Buckets', stats.total, 'inbox', '#6a4fae'],
            ['Public', stats.pub, 'grid', '#2c7a45'],
            ['Private', stats.priv, 'shield', '#c96342'],
            ['Owners', stats.owners, 'users', 'var(--gos-text-3)'],
          ].map(([label, val, icon, col]) => (
            <div key={label} className="gos-tm-stat">
              <span className="gos-tm-stat-ic" style={{ color: col }}><Ic name={icon} size={17} /></span>
              <div>
                <div className="gos-tm-stat-val">{val}</div>
                <div className="gos-tm-stat-label">{label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="gos-tm-toolbar">
          <div className="gos-tm-search">
            <Ic name="search" size={16} />
            <input placeholder="Search buckets…" value={q} onChange={(e) => setQ(e.target.value)} />
            {q ? <button type="button" className="gos-tm-clear" onClick={() => setQ('')}><Ic name="x" size={14} /></button> : null}
          </div>
          <span className="gos-tm-count"><strong>{rows.length}</strong> buckets</span>
        </div>

        {loading ? (
          <div className="gos-tm-grid">
            {Array.from({ length: 6 }).map((_, i) => <BucketCardSkeleton key={i} />)}
          </div>
        ) : rows.length === 0 ? (
          <div className="gos-tm-cardbox"><div className="gos-tm-empty">{buckets.length ? 'No buckets match your search.' : 'No buckets yet — click New Bucket.'}</div></div>
        ) : (
          <div className="gos-tm-grid">
            {rows.map((b) => {
              const tint = colorFor(b.id);
              const oid = ownerIdOf(b);
              const priv = isPrivate(b);
              return (
                <div key={b.id} className="gos-tm-card" onClick={() => setOpenId(b.id)}>
                  <div className="gos-tm-card-accent" style={{ background: tint }} />
                  <div className="gos-tm-card-head">
                    <span className="gos-tm-card-badge" style={{ background: `${tint}1f`, color: tint }}><Ic name="inbox" size={18} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="gos-tm-card-name">{b.name}</div>
                      <div className="gos-tm-card-region">
                        <Ic name="leads" size={12} />
                        {accessOf(b) ? `Access ${accessOf(b)}` : 'Access —'}
                        {' · '}
                        {priv ? 'Private' : 'Public'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="gos-tm-icon-btn"
                      title="Edit bucket"
                      onClick={(e) => { e.stopPropagation(); openEdit(b); }}
                    >
                      <Ic name="edit" size={15} />
                    </button>
                  </div>
                  <div className="gos-tm-leads">
                    <div className="gos-tm-lead">
                      <Avatar name={userName[oid]} size={28} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="gos-tm-lead-name">{userName[oid] || 'Unassigned'}</div>
                        <div className="gos-tm-lead-role"><Ic name="star" size={10} />Owner</div>
                      </div>
                      <span className="gos-tm-role-tag owner">Owner</span>
                    </div>
                  </div>
                  <div className="gos-tm-foot">
                    {accessOf(b) ? <span className="gos-tm-type">{accessOf(b)}</span> : <span className="gos-tm-muted">No level</span>}
                    <span className={'gos-tm-role-tag ' + (priv ? 'member' : 'captain')}>{priv ? 'Private' : 'Public'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AddBucketForm
        open={formOpen}
        editData={editData}
        usersList={usersList}
        onClose={() => setFormOpen(false)}
        onSave={() => { setFormOpen(false); loadBuckets(); }}
      />
    </Wrap>
  );
}

/* ── per-bucket detail: hero + Leads / Object-Assignment tabs ── */
function BucketDetail({ bucket, userName, onBack, onEdit }) {
  const [tab, setTab] = useState('leads');
  const tint = colorFor(bucket.id);
  const oid = ownerIdOf(bucket);
  const priv = isPrivate(bucket);

  // leads
  const [allLeads, setAllLeads] = useState([]);
  const [bucketLeads, setBucketLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsToAssign, setLeadsToAssign] = useState([]);
  const [savingLeads, setSavingLeads] = useState(false);

  // objects
  const [objects, setObjects] = useState([]);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [objectsLoadedFor, setObjectsLoadedFor] = useState(null);

  useEffect(() => {
    apiServices
      .getLeadList({ pageSize: 500, pageNumber: 1, columnName: 'UpdatedDateTime', orderType: 'desc', filterJson: null })
      .then((r) => setAllLeads(r.data.leads || []))
      .catch((e) => console.error('getLeadList', e));
  }, []);

  const loadBucketLeads = () => {
    setLeadsLoading(true);
    apiServices
      .getLeadsByBucket(bucket.id)
      .then((r) => {
        const raw = r.data;
        setBucketLeads(Array.isArray(raw) ? raw : raw?.leads || raw?.data || []);
      })
      .catch((e) => console.error('getLeadsByBucket', e))
      .finally(() => setLeadsLoading(false));
  };
  useEffect(() => { loadBucketLeads(); /* eslint-disable-next-line */ }, [bucket.id]);

  const loadBucketObjects = () => {
    setObjectsLoading(true);
    apiServices
      .getBucketObjects(bucket.id)
      .then((r) => {
        const raw = r.data;
        setObjects(Array.isArray(raw) ? raw : raw?.bucketObjects || raw?.data || []);
        setObjectsLoadedFor(bucket.id);
      })
      .catch((e) => console.error('getBucketObjects', e))
      .finally(() => setObjectsLoading(false));
  };

  // Load once per bucket, not on every Leads <-> Object Assignment tab switch —
  // only a genuine bucket change (objectsLoadedFor mismatch) should trigger a refetch.
  useEffect(() => {
    if (tab !== 'objects') return;
    if (objectsLoadedFor === bucket.id) return;
    loadBucketObjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, bucket.id, objectsLoadedFor]);

  const leadOptions = useMemo(
    () => allLeads.map((l) => ({
      value: l.id,
      label: `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.fullName || l.companyName || l.email || l.id,
      meta: { company: l.companyName, email: l.email },
      accessLevel: l.accessLevel,
    })),
    [allLeads]
  );

  // Only leads at or below this bucket's access level can be assigned to it
  // (e.g. an L2 bucket can take L1 and L2 leads, but not L3) — the counterpart to
  // AddBucketForm's "assign users must be >= bucket level" rule. Bucket rank comes
  // from the bucket record directly.
  const availableLeadOptions = useMemo(() => {
    const assigned = new Set(bucketLeads.map((l) => l.objectId?.toLowerCase()));
    const bucketRank = ACCESS_RANK[accessOf(bucket)] ?? null;
    return leadOptions.filter((o) => {
      if (assigned.has(o.value?.toLowerCase())) return false;
      if (bucketRank && (ACCESS_RANK[o.accessLevel] ?? 0) > bucketRank) return false;
      return true;
    });
  }, [leadOptions, bucketLeads, bucket]);

  const saveLeads = () => {
    if (!leadsToAssign.length) return;
    setSavingLeads(true);
    apiServices
      .addMappingToBucket({ bucketId: bucket.id, objectType: 'Lead', assignedToUserId: null, objectIds: leadsToAssign.map((l) => l.value) })
      .then(() => {
        toast.success('Leads assigned successfully!', { position: 'top-right' });
        setLeadsToAssign([]);
        loadBucketLeads();
        // newly-assigned leads also show up as objects — invalidate so the
        // Object Assignment tab refetches next time it's viewed
        setObjectsLoadedFor(null);
      })
      .catch((error) => toast.error(error?.response?.data?.message || error.message || 'Failed to assign leads', { position: 'top-right' }))
      .finally(() => setSavingLeads(false));
  };

  return (
    <>
      <div className="gos-tm-detail-top">
        <button type="button" className="gos-tm-back" onClick={onBack}><Ic name="chevL" size={17} />Buckets</button>
        <button type="button" className="gos-btn gos-btn-ghost" onClick={onEdit}><Ic name="edit" size={15} />Edit bucket</button>
      </div>

      <div className="gos-tm-cardbox gos-tm-hero">
        <span className="gos-tm-card-badge" style={{ background: `${tint}1f`, color: tint, width: 52, height: 52 }}><Ic name="inbox" size={22} /></span>
        <div className="gos-tm-hero-meta">
          <div className="gos-tm-hero-name">{bucket.name}</div>
          <div className="gos-tm-hero-sub">
            <Ic name="leads" size={13} />
            {accessOf(bucket) ? `Access ${accessOf(bucket)}` : 'Access —'}{' · '}{priv ? 'Private' : 'Public'}
          </div>
        </div>
        <div className="gos-tm-hero-people">
          {oid ? <div className="gos-tm-hero-person"><Avatar name={userName[oid]} size={26} /><span>{userName[oid]}</span><span className="gos-tm-role-tag owner">Owner</span></div> : null}
          <span className={'gos-tm-role-tag ' + (priv ? 'member' : 'captain')}>{priv ? 'Private' : 'Public'}</span>
        </div>
      </div>

      <div className="gos-tm-tabs">
        <button type="button" className={'gos-tm-tab' + (tab === 'leads' ? ' active' : '')} onClick={() => setTab('leads')}>
          <Ic name="target" size={15} />Leads<span className="gos-tm-tabcount">{bucketLeads.length}</span>
        </button>
        <button type="button" className={'gos-tm-tab' + (tab === 'objects' ? ' active' : '')} onClick={() => setTab('objects')}>
          <Ic name="grid" size={15} />Object Assignment
        </button>
      </div>

      {tab === 'leads' ? (
        <div className="gos-tm-cardbox" style={{ overflow: 'visible', padding: 0 }}>
          <div className="gos-tm-addbar">
            <div style={{ flex: 1 }}>
              <Select
                isMulti options={availableLeadOptions} value={leadsToAssign} onChange={setLeadsToAssign}
                placeholder="Search and select leads…" closeMenuOnSelect={false} hideSelectedOptions={false}
                menuPortalTarget={document.body} styles={gosSelect}
                formatOptionLabel={(opt) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{opt.label}</div>
                    {(opt.meta?.company || opt.meta?.email) ? (
                      <div style={{ color: 'var(--gos-text-3)', fontSize: '.73rem' }}>
                        {[opt.meta.company, opt.meta.email].filter(Boolean).join(' · ')}
                      </div>
                    ) : null}
                  </div>
                )}
              />
            </div>
            <button type="button" className="gos-btn gos-btn-primary" disabled={savingLeads || !leadsToAssign.length} onClick={saveLeads}>
              {savingLeads ? 'Saving…' : 'Save Leads'}
            </button>
          </div>
          {leadsLoading ? (
            <TableSkeleton cols={6} />
          ) : (
            <>
              <div className="gos-tm-tblscroll">
                <table className="gos-tm-tbl">
                  <thead>
                    <tr><th>Name</th><th>Company</th><th>Object Type</th><th>Email</th><th>Phone</th><th>Owner</th></tr>
                  </thead>
                  <tbody>
                    {bucketLeads.map((l, i) => (
                      <tr key={l.objectId || i}>
                        <td><span className="gos-tm-cellname">{l.fullName || `${l.firstName || ''} ${l.lastName || ''}`.trim() || '—'}</span></td>
                        <td><span className="gos-tm-cell gos-tm-muted">{l.companyName || '—'}</span></td>
                        <td><span className="gos-tm-type">{l.objectType || 'Lead'}</span></td>
                        <td><span className="gos-tm-cell gos-tm-muted">{l.email || '—'}</span></td>
                        <td><span className="gos-tm-mono">{l.phone || l.directPhone || l.mobile || '—'}</span></td>
                        <td>{l.owner ? <div className="gos-tm-row" style={{ gap: 8 }}><Avatar name={l.owner} size={22} /><span className="gos-tm-cell gos-tm-muted">{l.owner}</span></div> : <span className="gos-tm-muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {bucketLeads.length === 0 ? <div className="gos-tm-empty">No leads assigned to this bucket yet.</div> : null}
            </>
          )}
        </div>
      ) : null}

      {tab === 'objects' ? (
        <div className="gos-tm-cardbox" style={{ overflow: 'visible', padding: 0 }}>
          {objectsLoading ? (
            <TableSkeleton cols={3} />
          ) : (
            <>
              <div className="gos-tm-tblscroll">
                <table className="gos-tm-tbl gos-tm-objtbl">
                  <thead>
                    <tr><th>Object ID</th><th>Object Type</th><th>Object Name</th></tr>
                  </thead>
                  <tbody>
                    {objects.map((o, i) => (
                      <tr key={o.id || o.objectId || i}>
                        <td><span className="gos-tm-mono gos-tm-objid" title={o.objectId}>{o.objectId}</span></td>
                        <td><span className="gos-tm-type">{o.objectType || 'Lead'}</span></td>
                        <td><span className="gos-tm-cellname">{o.objectName || '—'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {objects.length === 0 ? <div className="gos-tm-empty">No objects assigned to this bucket yet.</div> : null}
            </>
          )}
        </div>
      ) : null}
    </>
  );
}

export default BucketManagement;
